import { OmneoList, OmneoListDefinitionType, OmneoListItem, typeInStoreItemSupplementalParsed, typeInStoreItemSupplementalRaw } from '../types'
import { OmneoListItemInStore } from '../types/omneo-wishlist-item'
import { type_GM_STATE } from '../../helpers'
import { getManyProductsByIds, getManyProductsByManySkus } from '../../helpers/storefront-graphql-queries'
import { executeGraphQlQuery, getIdFromGqlAsNumber } from '../../helpers/storefront-graphql-helper'

declare var GM_STATE: type_GM_STATE

// known url for dummy image on Cin7 - don't have a better method than this
const knownCin7DefaultImageUrls = [
  'https://go.cin7.com/webfiles/SarahandSebastiaAU/webpages/images/621637/59111_PriceBreak_59150.jpg',
  'https://go.cin7.com/webfiles/SarahandSebastiaAU/webpages/images/621637/55710_PriceBreak_55749.jpg'
]

export const hasOwnProperties = (property: string, obj: object) => {
  return Object.prototype.hasOwnProperty.call(obj, property)
}

type WishlistComparableProperty = Record<string, any> & {
  properties: Record<string, any>
}

const WISHLIST_PROPERTY_ENTRY_KEYS = new Set([
  'core_product_multi_id',
  'core_product_single_id',
  'form_type',
  'id',
  'option-0',
  'option-1',
  'option-2',
  'option-3',
  'product-id',
  'properties',
  'quantity',
  'section-id',
  'utf8'
])

function isComparableRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sortObjectKeys<T extends Record<string, any>>(value: T): T {
  return Object.keys(value)
    .sort()
    .reduce(function (result: Record<string, any>, key) {
      result[key] = value[key]
      return result
    }, {}) as T
}

function parseComparableProperties(rawProperties: unknown) {
  if (typeof rawProperties === 'string') {
    try {
      return JSON.parse(rawProperties)
    } catch {
      return []
    }
  }

  return rawProperties
}

function normalizePropertyBag(propertyBag: unknown) {
  if (Array.isArray(propertyBag)) {
    return propertyBag.reduce((result: Record<string, any>, value) => {
      if (!isComparableRecord(value)) {
        return result
      }

      return {
        ...result,
        ...value
      }
    }, {})
  }

  if (isComparableRecord(propertyBag)) {
    return propertyBag
  }

  return {}
}

function toComparablePropertyList(rawProperties: unknown): Record<string, any>[] {
  const parsedProperties = parseComparableProperties(rawProperties)

  if (Array.isArray(parsedProperties)) {
    return parsedProperties.filter(isComparableRecord)
  }

  if (!isComparableRecord(parsedProperties)) {
    return []
  }

  const hasPropertyEntryKeys = Object.keys(parsedProperties).some((key) => WISHLIST_PROPERTY_ENTRY_KEYS.has(key))

  return [hasPropertyEntryKeys ? parsedProperties : { properties: parsedProperties }]
}

export function normalizeWishlistComparableProperties(rawProperties: unknown = []) {
  return toComparablePropertyList(rawProperties)
    .map((property: Record<string, any>) => {
      const normalizedProperty: WishlistComparableProperty = {
        ...property,
        properties: sortObjectKeys(normalizePropertyBag(property.properties))
      }

      if (normalizedProperty.hasOwnProperty('section-id')) {
        delete normalizedProperty['section-id']
      }
      if (normalizedProperty.properties.hasOwnProperty('_uniqueTimeStamp')) {
        normalizedProperty.properties._uniqueTimeStamp = 1
      }
      if (normalizedProperty.properties.hasOwnProperty('_uniqueItem')) {
        normalizedProperty.properties._uniqueItem = 1
      }
      if (normalizedProperty.properties.hasOwnProperty('pairId')) {
        normalizedProperty.properties.pairId = 1
      }

      delete normalizedProperty.form_type
      delete normalizedProperty.id
      delete normalizedProperty.quantity
      delete normalizedProperty.utf8
      delete normalizedProperty['product-id']
      delete normalizedProperty['option-1']
      delete normalizedProperty['option-2']

if (normalizedProperty.hasOwnProperty('core_product_single_id') && normalizedProperty.hasOwnProperty('core_product_multi_id')) {
         delete normalizedProperty['option-0']
       }
      return sortObjectKeys(normalizedProperty)
    })
    .filter((property: WishlistComparableProperty) => {
      const propertyKeys = Object.keys(property)

      if (!propertyKeys.length) {
        return false
      }

      if (propertyKeys.length === 1 && propertyKeys[0] === 'properties') {
        return Object.keys(property.properties || {}).length > 0
      }

      return true
    })
    .sort((propertyA: WishlistComparableProperty, propertyB: WishlistComparableProperty) => {
      return JSON.stringify(propertyA).localeCompare(JSON.stringify(propertyB))
    })
}

/**
 * Child items such as charms, letters, engravings should not be shown in the wishlist; nor items missing skus
 */
export const itemShouldBeHidden = (item: OmneoListItem | OmneoListItemInStore): boolean => {
  // to find the patterns of sku and title to use, run:
  /*
    let lists = await omneoService.sdk.get('lists').then(res => res.body.data)
    let allItems = lists.flatMap(list => list.items)
    let targetProps = allItems.map(item => { 
        const { title, sku } = item.product_variant
        return { title, sku }
    })
    console.table(targetProps)
  */

  const hideChildItemFragments = GM_STATE.wishlist.hideWishlistItemsBySkuFragment
  const { title, sku } = item.product_variant

  const titleContainsFragment = hideChildItemFragments.some((fragment) => {
    if (fragment === '') return false
    return title.includes(fragment)
  })
  const skuContainsFragment = hideChildItemFragments.some((fragment) => {
    if (fragment === '') return false
    return sku.includes(fragment)
  })
  const itemIsMissingSku = !item.product_variant.sku

  if (titleContainsFragment || skuContainsFragment || itemIsMissingSku) {
    return true
  }

  return false
}

/**
 * TODO - give this function a better name. It's scanning for item meta, not list validity
 */
export function listMadeOnSiteIsValid(list: OmneoList): boolean {
  // filter out any storemade list, with items, which has malformed meta props
  if (list.definition.type !== 'default') return true

  // allow empty lists
  if (!list.items.length) return true

  // only if all data is malformed - has bigger effect in detail page, causes a blank screen
  if (list.items.every((item) => item.meta === null)) return false

  return true
}

export function getWishlistListCoverImage(list: OmneoList): { src: string; alt: string } {
  const { imageDefault } = GM_STATE.wishlist
  let imageSrc =
    list.meta?.image?.src || list.items.find((x) => x?.meta?.image?.src)?.meta.image.src || list.items.find((x) => x?.meta?.image)?.meta.image || ''

  if (!imageSrc) {
    const itemWithImage = list.items.find((item) => {
      if (itemShouldBeHidden(item)) return false

      const firstImageUrl = item.product?.images?.[0]?.url
      // known url for dummy image on Cin7 - don't have a better method than this
      if (knownCin7DefaultImageUrls.includes(firstImageUrl)) return false

      return item.product?.images?.[0]?.url
    })
    if (itemWithImage) {
      imageSrc = itemWithImage.product.images[0].url
    }
  }

  const image = {
    src: imageSrc?.toString() || imageDefault.src,
    alt: list.name || imageDefault.alt || ''
  }

  return image
}

function getActiveCountry(): string {
  return typeof (globalThis as any)?.Shopify?.country === 'string'
    ? (globalThis as any).Shopify.country
    : ''
}

export async function getStorefrontProductsBySkus(skus: string[]): Promise<Array<typeInStoreItemSupplementalParsed>> {
  return fetchStorefrontProducts(getManyProductsByManySkus(skus, getActiveCountry()), (data) => data.data.search.nodes)
    .catch((err) => {
      console.error('Could not fetch products from storefront during getLists', err)
      return []
    })
}

const STOREFRONT_ID_CHUNK_SIZE = 50

export async function fetchStorefrontProductsByIdsStrict(productIds: Array<string | number>): Promise<Array<typeInStoreItemSupplementalParsed>> {
  const chunks: Array<Array<string | number>> = []
  for (let index = 0; index < productIds.length; index += STOREFRONT_ID_CHUNK_SIZE) {
    chunks.push(productIds.slice(index, index + STOREFRONT_ID_CHUNK_SIZE))
  }

  const results = await Promise.all(
    // onlineStoreUrl is null when the product isn't published to the Online Store channel - treat it as missing
    chunks.map((chunk) =>
      fetchStorefrontProducts(getManyProductsByIds(chunk, getActiveCountry()), (data) =>
        data.data.nodes.filter((node: typeInStoreItemSupplementalRaw | null) => node?.onlineStoreUrl)
      )
    )
  )
  return results.flat()
}

type OnlineStoreAjaxProduct = {
  id: number
  title: string
  handle: string
  tags: string[] | string
  options: Array<string | { name: string }>
  images: string[]
  variants: Array<{
    id: number
    sku: string | null
    price: number
    available: boolean
    options: string[]
    featured_image?: { src: string; alt: string | null } | null
  }>
}

const toAbsoluteUrl = (url: string) => (url.startsWith('//') ? `https:${url}` : url)

function getItemHandle(item: OmneoListItem | OmneoListItemInStore) {
  if (typeof item.meta?.handle === 'string' && item.meta.handle) {
    return item.meta.handle
  }

  const productUrl = typeof item.product?.external_id === 'string' ? item.product.external_id : ''
  return productUrl.match(/\/products\/([^/?#]+)/)?.[1] || ''
}

async function fetchOnlineStoreProduct(handle: string): Promise<typeInStoreItemSupplementalParsed | null> {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`)

  if (!response.ok) {
    return null
  }

  const product: OnlineStoreAjaxProduct = await response.json()
  const optionNames = product.options.map((option) => (typeof option === 'string' ? option : option.name))
  const variants = product.variants.map((variant) => ({
    id: `gid://shopify/ProductVariant/${variant.id}`,
    availableForSale: variant.available,
    sku: variant.sku || '',
    price: (variant.price / 100).toString(),
    selectedOptions: variant.options.map((value, index) => ({ name: optionNames[index] || '', value })),
    image: variant.featured_image?.src ? { url: toAbsoluteUrl(variant.featured_image.src), altText: variant.featured_image.alt } : null
  }))

  return {
    id: `gid://shopify/Product/${product.id}`,
    title: product.title,
    handle: product.handle,
    tags: Array.isArray(product.tags) ? product.tags : product.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    productBiline: '',
    images: product.images.slice(0, 2).map((url) => ({ url: toAbsoluteUrl(url), altText: null })),
    variants,
    skus: variants.map((variant) => variant.sku)
  }
}

export async function addOnlineStoreFallbackProducts(
  items: Array<OmneoListItem | OmneoListItemInStore>,
  storefrontProducts: typeInStoreItemSupplementalParsed[]
): Promise<typeInStoreItemSupplementalParsed[]> {
  const foundProductIds = new Set(storefrontProducts.map((product) => getIdFromGqlAsNumber(product.id)))
  const missingHandles = [...new Set(
    items
      .filter((item) => item.meta?.productId && !foundProductIds.has(Number(item.meta.productId)))
      .map(getItemHandle)
      .filter(Boolean)
  )]

  if (!missingHandles.length) {
    return storefrontProducts
  }

  const fallbackProducts = await Promise.all(
    missingHandles.map((handle) => fetchOnlineStoreProduct(handle).catch(() => null))
  )

  return [
    ...storefrontProducts,
    ...fallbackProducts.filter((product): product is typeInStoreItemSupplementalParsed => Boolean(product))
  ]
}

async function fetchStorefrontProducts(
  gqlQuery: { query: string; variables?: any },
  getNodes: (data: any) => Array<typeInStoreItemSupplementalRaw | null>
): Promise<Array<typeInStoreItemSupplementalParsed>> {
  return await executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) {
        console.error('res not ok', res)
        throw res
      }
      return res.json()
    })
    .then((data) => {
      if (data.errors) {
        throw data
      }

      const products = getNodes(data)
        .filter((product): product is typeInStoreItemSupplementalRaw => Boolean(product))
        .map((product: typeInStoreItemSupplementalRaw) => {
          if (!product.handle || !product.variants) {
            console.error('Could not find handle or variants on product node while enriching wishlist items', product)
            return null
          }
          const variants = product.variants.nodes.map((variant) => {
            return {
              id: variant.id,
              availableForSale: variant.availableForSale,
              sku: variant.sku,
              price: variant.price.amount,
              selectedOptions: variant.selectedOptions,
              image: variant.image || null
            }
          })
          const skus = variants.map((x) => x.sku)

          return {
            id: product.id,
            title: product.title,
            handle: product.handle,
            tags: Array.isArray(product.tags) ? product.tags : [],
            productBiline: product.productBiline?.value || '',
            images: product.images?.nodes || [],
            variants,
            skus
          }
        })
        // filter out nullish
        .filter((x): x is typeInStoreItemSupplementalParsed => Boolean(x))

      return products
    })
}

export function attachRequiredStorefrontInformationToOmneoItem(
  item: OmneoListItem | OmneoListItemInStore,
  storefrontProducts: typeInStoreItemSupplementalParsed[],
  _listDefinition: OmneoListDefinitionType
): OmneoListItem | OmneoListItemInStore {
  const sku = item.product_variant.sku
  const itemProductId = item.meta?.productId
  const itemVariantId = item.meta?.variantId
  const storefrontProduct =
    storefrontProducts.find((product) => Boolean(itemProductId) && product.id.endsWith(`/${itemProductId}`)) ||
    storefrontProducts.find((product) => product.skus.includes(sku))

  item.meta = item.meta || {}

  if (!storefrontProduct) {
    if (item.product?.status && item.product.status !== 'active') {
      item.meta.hidden = true
      return item
    }

    item.meta.discontinued = true
    item.meta.variantAvailable = false
    return item
  }

  const storefrontVariant =
    storefrontProduct.variants.find((variant) => Boolean(itemVariantId) && variant.id.endsWith(`/${itemVariantId}`)) ||
    storefrontProduct.variants.find((variant) => variant.sku === sku)
  const storefrontVariantPrice = storefrontVariant?.price ?? ''
  const variantId = getIdFromGqlAsNumber(storefrontVariant?.id || '')
  const productId = getIdFromGqlAsNumber(storefrontProduct?.id || '')

  delete item.meta.discontinued
  delete item.meta.hidden

  // CDSD-29896
  // always need to forcefully set the price - the Omneo list currently stores the price, but doesn't know if its correct or not
  if (storefrontVariantPrice) {
    item.meta.variantPrice = storefrontVariantPrice
  }

  item.meta.variantAvailable = storefrontVariant?.availableForSale || false

  const handle = storefrontProduct?.handle || ''
  const title = storefrontProduct?.title || item.product.title
  const tags = storefrontProduct?.tags || []

  const productBiline = storefrontProduct?.productBiline || ''
  // TODO: get variant price with particular sku

  item.product.handle = handle
  item.product.title = title
  item.product.tags = tags

  item.meta.productBiline = productBiline
  item.meta.handle = handle
  if (typeof item.meta.templateSuffix !== 'string' || !item.meta.templateSuffix.trim()) {
    item.meta.templateSuffix = 'default'
  }
  item.meta.variantOptions = storefrontVariant?.selectedOptions.map((option) => option.value) || []
  item.meta.variantSku = storefrontVariant?.sku || sku
  item.meta.variantId = variantId
  item.meta.productId = productId

  const images = [storefrontVariant?.image, ...storefrontProduct.images]
    .filter((image): image is { url: string; altText: string | null } => Boolean(image?.url))
    .filter((image, index, list) => list.findIndex((candidate) => candidate.url === image.url) === index)
    .slice(0, 2)

  if (images.length) {
    item.product.images = images.map((image, index) => ({ url: image.url, sort_order: index }))
    item.meta.image = { src: images[0].url, alt: images[0].altText || title }
  }

  return item
}
